import { Injectable } from '@angular/core';
import { DownloadFormat } from '../shared';
import { endpoints } from './api-endpoints';
import { IGetDeviceAnalysisSectionDataRequest, IPagingParameters } from './interfaces';

@Injectable({
  providedIn: 'root'
})
export class ApiUtils {
  private baseApiUrl!: string;
  private endpoints = endpoints;

  init(baseApiUrl: string): void {
    this.baseApiUrl = baseApiUrl;
    if (!this.baseApiUrl.endsWith('/')) {
      this.baseApiUrl += '/';
    }
  }

  getSearchDeviceEventsUrl(): string {
    return this.getUrl(this.endpoints.deviceEvents.search);
  }

  getAcknowledgeDeviceEventUrl(deviceEventId: string): string {
    return this.getUrl(this.replacePlaceholders(this.endpoints.deviceEvents.acknowledge, { deviceEventId: deviceEventId }));
  }

  getAcknowledgeAllDeviceEventsUrl(): string {
    return this.getUrl(this.endpoints.deviceEvents.acknowledgeAll);
  }

  getUploadEncryptionKeysExportedDataUrl(): string {
    return this.getUrl(this.endpoints.encryptionKeys.uploadExportedData);
  }

  getGetEncryptionKeysUrl(): string {
    return this.getUrl(this.endpoints.encryptionKeys.get);
  }
  getSearchUserProfileWithRolesUrl(): string {
    return this.getUrl(this.endpoints.users.profileWithRoles);
  }
  getSetEncryptionKeysUrl(): string {
    return this.getUrl(this.endpoints.encryptionKeys.set);
  }

  getGetTemporaryTokenUrl(): string {
    return this.getUrl(this.endpoints.auth.getTemporaryToken);
  }

  getGetDeviceAnalysisSectionDataAsFileUrl(
    jobId: string, format: DownloadFormat, trackColor: string, req: IGetDeviceAnalysisSectionDataRequest, token: string
  ) {
    const urlWithoutQuery = this.getUrl(this.replacePlaceholders(this.endpoints.jobData.getDeviceAnalysisSectionDataAsFile, jobId));
    const url = this.getURLFromPath(urlWithoutQuery);
    url.searchParams.append('format', format);
    url.searchParams.append('trackColor', trackColor);
    url.searchParams.append('from', req.dateRange.from);
    url.searchParams.append('to', req.dateRange.to);
    url.searchParams.append('grouping', '' + req.grouping);
    url.searchParams.append('dataType', '' + req.dataType);
    url.searchParams.append('dataSelection', '' + req.dataSelection);
    url.searchParams.append('maximumHoldingTimeMinutes', '' + req.maximumHoldingTimeMinutes);
    url.searchParams.append('minimumSpeedKilometersPerHour', '' + req.minimumSpeedKilometersPerHour);
    url.searchParams.append('onlyPositionsWithHighAccuracy', '' + req.onlyPositionsWithHighAccuracy);
    if (req.devicePAIPAddress) {
      url.searchParams.append('devicePAIPAddress', req.devicePAIPAddress);
    }
    if (req.vehicleId) {
      url.searchParams.append('vehicleId', req.vehicleId);
    }
    url.searchParams.append('token', token);
    return url.href;
  }

  getGetDeviceAnalysisSectionDataUrl(jobId: string): string {
    return this.getUrl(this.replacePlaceholders(this.endpoints.jobData.getDeviceAnalysisSectionData, jobId));
  }

  getAnalysisRendezvousUrl(jobId: string): string {
    return this.getUrl(this.replacePlaceholders(this.endpoints.jobData.getAnalysisRendezvous, jobId));
  }

  getGetDeviceAnalysisSectionsUrl(jobId: string): string {
    return this.getUrl(this.replacePlaceholders(this.endpoints.jobData.getDeviceAnalysisSections, jobId));
  }

  getGetDeviceLogPositionsForJobAsFileUrl(
    jobId: string, deviceId: string, from: string, to: string, trackColor: string, format: string, token: string
  ) {
    return this.getUrl(this.replacePlaceholders(this.endpoints.jobData.getDeviceLogPositionsForJobAsFile, {
      jobId: jobId, deviceId: deviceId, from: from, to: to, trackColor: trackColor, format: format,
      token: token
    }));
  }

  getGetDeviceLivePositionsForJobAsFileUrl(
    jobId: string, deviceId: string, from: string, to: string, trackColor: string, format: string, token: string
  ) {
    return this.getUrl(this.replacePlaceholders(this.endpoints.jobData.getDeviceLivePositionsForJobAsFile, {
      jobId: jobId, deviceId: deviceId, from: from, to: to, trackColor: trackColor, format: format,
      token: token
    }));
  }

  getSearchTrackingServerDiagnosticsUrl(): string {
    return this.getUrl(this.endpoints.trackingServerDiagnostics.search);
  }

  getGetClientSettingsUrl(): string {
    return this.getUrl(this.endpoints.clientSettings.get);
  }

  getReverseGeocodeUrl(lat: number, lon: number): string {
    return this.getUrlWithPlaceholders(this.endpoints.geocode.reverse, { lat: lat, lon: lon });
  }

  getGetServerInfoUrl(): string {
    return this.getUrl(this.endpoints.serverInfo.get);
  }

  getGetApiRequestIPAddressUrl(): string {
    return this.getUrl(this.endpoints.apiRequests.getIPAddress);
  }

  getGetConnectionEventIPAddressUrl(): string {
    return this.getUrl(this.endpoints.connectionEvents.getIPAddress);
  }

  getGetLastApiUserActivitiesUrl(): string {
    return this.getUrl(this.endpoints.apiRequests.getLastApiUserActivities);
  }

  getGetDeviceRecordsCountWithMinMaxDatesUrl(): string {
    return this.getUrl(this.endpoints.deviceRecordsStatistics.getDeviceRecordsCountWithMinMaxDates);
  }

  getGetDeviceRecordsCountWithMinMaxDatesForDatesInThePastUrl(): string {
    return this.getUrl(this.endpoints.deviceRecordsStatistics.getDeviceRecordsCountWithMinMaxDatesForDatesInThePast);
  }

  getGetDeviceRecordsCountWithMinMaxDatesForDatesInTheFutureUrl(): string {
    return this.getUrl(this.endpoints.deviceRecordsStatistics.getDeviceRecordsCountWithMinMaxDatesForDatesInTheFuture);
  }

  getGetLastConnectionActivitiesUrl(): string {
    return this.getUrl(this.endpoints.connectionEvents.getLastConnectionActivities);
  }

  getSearchDevicesUrl(): string {
    return this.getUrl(this.endpoints.devices.search);
  }
  getFilterDevicesUrl(filter:string): string {
    return this.getUrlWithPlaceholders(this.endpoints.devices.filter, filter);
  }
  getSearchUsersUrl(): string {
    return this.getUrl(this.endpoints.users.search);
  }

  getGetMyMissionDevicesUrl(): string {
    return this.getUrl(this.endpoints.users.getMyMissionDevices);
  }

  getUpdateSettingsUrl(): string {
    return this.getUrl(this.endpoints.settings.update);
  }

  getGetAllSettingsUrl(): string {
    return this.getUrl(this.endpoints.settings.getAll);
  }

  getGetIsPAIPAddressAvailableUrl(paipAddress: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.devices.getIsPAIPAddressAvailable, paipAddress);
  }

  getDeleteRoleUrl(roleId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.roles.delete, roleId);
  }

  getGetUsersInRoleUrl(roleId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.roles.getUsersInRole, roleId);
  }

  getRemovePermissionFromRoleUrl(roleId: string, permissionId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.roles.removePermissionFromRole, {
      roleId: roleId, permissionId: permissionId
    });
  }

  getAddPermissionToRoleUrl(roleId: string, permissionId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.roles.addPermissionToRole, {
      roleId: roleId, permissionId: permissionId
    });
  }

  getGetPermissionsUrl(): string {
    return this.getUrl(this.endpoints.permissions.getAll);
  }

  getGetRolePermissionsUrl(roleId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.roles.getPermissions, roleId);
  }

  getUpdateRoleUrl(roleId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.roles.update, roleId);
  }

  getGetRoleUrl(roleId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.roles.get, roleId);
  }

  getGetIsRoleNameAvailableUrl(roleName: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.roles.getIsRoleNameAvailable, roleName);
  }

  getCreateRoleUrl(): string {
    return this.getUrl(this.endpoints.roles.create);
  }

  getGetDevicesLastLivePositionForJobUrl(jobId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.jobData.getDevicesLastLivePositionForJob, jobId);
  }

  getGetDeviceLogPositionsForJobUrl(jobId: string, deviceId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.jobData.getDeviceLogPositionsForJob, {
      jobId: jobId, deviceId: deviceId
    });
  }

  getGetDeviceLivePositionsForJobUrl(jobId: string, deviceId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.jobData.getDeviceLivePositionsForJob, {
      jobId: jobId, deviceId: deviceId
    });
  }

  getRefreshTokenUrl(): string {
    return this.getUrl(this.endpoints.auth.refreshToken);
  }

  getUnassignRoleFromUserUrl(userId: string, systemRoleId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.users.unassignRoleFromUser, {
      userId: userId, systemRoleId: systemRoleId
    });
  }

  getAssignRoleToUserUrl(userId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.users.assignRoleToUser, userId);
  }

  getGetUserRolesUrl(userId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.users.getRoles, userId);
  }

  getAddResetPasswordCodeUrl(userId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.users.addResetPasswordCode, userId);
  }

  getUpdateUserProfileUrl(userId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.users.updateProfile, userId);
  }

  getGetUserProfileUrl(userId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.users.getProfile, userId);
  }

  getActivateUserUrl(userId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.users.activate, userId);
  }

  getDeactivateUserUrl(userId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.users.deactivate, userId);
  }

  getGetUserUrl(userId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.users.get, userId);
  }

  getGetAllRolesOfTypeSystemUrl(): string {
    return this.getUrl(this.endpoints.roles.getAllOfTypeSystem);
  }

  getGetAllRolesByJobContextUrl(): string {
    return this.getUrl(this.endpoints.roles.getAllByJobContext);
  }

  getGetAllUsersByJobContextUrl(): string {
    return this.getUrl(this.endpoints.users.getAllByJobContext);
  }

  getRemoveUserFromJobUrl(userId: string, jobId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.jobs.removeJobUser, {
      userId: userId, jobId: jobId
    });
  }

  getGetJobUsersUrl(jobId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.jobs.getJobUsers, jobId);
  }

  getAddUserToJobUrl(jobId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.jobs.addUserToJob, jobId);
  }

  /* [Direct user->Tracking Unit assignment removed]
     User access is now managed exclusively through mission assignment.

  getUnassignUserFromDeviceUrl(deviceId: string, userId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.devices.unassignUser, {
      deviceId: deviceId, userId: userId
    });
  }

  getAssignUserToDeviceUrl(deviceId: string, userId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.devices.assignUser, {
      deviceId: deviceId, userId: userId
    });
  }

  getGetDeviceUsersUrl(deviceId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.devices.getUsers, deviceId);
  }
  */

  getUpdateDeviceUrl(deviceId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.devices.update, deviceId);
  }

  getGetDeviceUrl(deviceId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.devices.get, deviceId);
  }

  getGetDevicesUrl(): string {
    return this.getUrl(this.endpoints.devices.getAll);
  }

  getGetJobDevicesUrl(jobId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.jobs.getJobDevices, jobId);
  }

  getJobListConfigurationUrl(): string {
    return this.getUrl(this.endpoints.jobs.getJobListConfiguration);
  }

  getJobArchivesListUrl(): string {
    return this.getUrl(this.endpoints.jobs.getJobArchivesList);
  }

  getDeleteJobArchiveUrl(archiveId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.jobs.deleteJobArchive, archiveId);
  }

  getJobArchiveDownloadUrl(archiveId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.jobs.jobArchiveDownload, archiveId);
  }

  getJobArchiveRestoreUrl(archiveId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.jobs.jobArchiveRestoreUrl, archiveId);
  }

  getUploadArchiveUrl(): string {
    return this.getUrl(this.endpoints.jobs.jobArchiveUpload);
  }

  getArchiveJobUrl(jobId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.jobs.archiveJob, jobId);
  }

  getDeleteJobUrl(jobId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.jobs.deleteJob, jobId);
  }

  getJobLastArchivedAt(jobId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.jobs.getJobLastArchivedAt, jobId);
  }

  getGetCurrentJobConfigurationUrl(): string {
    return this.getUrl(this.endpoints.jobs.getCurrentJobConfiguration);
  }

  getGetJobVehiclesWithDeviceAssignmentsUrl(jobId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.jobs.getJobVehiclesWithDeviceAssignments, jobId);
  }

  getUpdateDeviceToVehicleAssignmentUrl(assignmentId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.jobs.updateDeviceToVehicleAssignment, assignmentId);
  }

  getAddDeviceToVehicleAssignmentUrl(): string {
    return this.getUrl(this.endpoints.jobs.addDeviceToVehicleAssignment);
  }

  getDeleteDeviceToVehicleAssignmentUrl(assignmentId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.jobs.deleteDeviceToVehicleAssignment, assignmentId);
  }

  getCreateDeviceUrl(): string {
    return this.getUrl(this.endpoints.devices.create);
  }

  getGetJobVehiclesUrl(jobId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.jobs.getJobVehicles, jobId);
  }

  getGetDevicesWithCurrentVehicle(jobId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.jobs.getJobDevicesWithCurrentVehicle, jobId);
  }

  getCreateVehicleUrl(): string {
    return this.getUrl(this.endpoints.vehicles.create);
  }

  getUpdateVehicleUrl(): string {
    return this.getUrl(this.endpoints.vehicles.update);
  }

  getUpdateJobUrl(jobId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.jobs.updateJob, jobId);
  }

  getGetJobUrl(jobId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.jobs.getJob, jobId);
  }

  getCreateJobUrl(): string {
    return this.getUrl(this.endpoints.jobs.create);
  }

  getSearchJobsUrl(): string {
    return this.getUrl(this.endpoints.jobs.search);
  }

  getResetPasswordUrl(username: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.users.resetPassword, username);
  }

  getCreateUserUrl(): string {
    return this.getUrl(this.endpoints.users.create);
  }

  getGetIsUsernameAvailableUrl(username: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.users.getIsUsernameAvailable, username);
  }

  getGetAllRolesUrl(): string {
    return this.getUrl(this.endpoints.roles.getAll);
  }

  getGetAllUsersUrl(): string {
    return this.getUrl(this.endpoints.users.getAll);
  }

  getGetJobTokenUrl(jobId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.auth.getJobToken, jobId);
  }

  getAuthenticateUrl(): string {
    return this.getUrl(this.endpoints.auth.authenticate);
  }

  getObjectWithoutId<T>(object: T): T {
    const result = { ...object };
    delete (result as any).id;
    return result;
  }

  getUserProfileSettingsUrl(userId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.userProfileSettings.get, userId);
  }

  getUpdateUserProfileUserTableSettingsUrl(userId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.userProfileSettings.updateUserTable, userId);
  }

  getUpdateUserProfileDeviceTableSettingsUrl(userId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.userProfileSettings.updateDeviceTable, userId);
  }

  getUpdateUserProfileRoleTableSettingsUrl(userId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.userProfileSettings.updateRoleTable, userId);
  }

  getUpdateUserProfileMissionTableSettingsUrl(userId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.userProfileSettings.updateMissionTable, userId);
  }

  getUpdateUserProfileAlarmNotificationsSettingsUrl(userId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.userProfileSettings.updateAlarmNotifications, userId);
  }

  getUpdateUserProfileMissionAnalysisSettingsUrl(userId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.userProfileSettings.updateMissionAnalysis, userId);
  }

  getUpdateUserProfileMisisonPositionSettingsUrl(userId: string): string {
    return this.getUrlWithPlaceholders(this.endpoints.userProfileSettings.updateMissionPosition, userId);
  }

  private getUrl(path: string): string {
    return this.baseApiUrl + path;
  }

  private getUrlWithPlaceholders(path: string, replacement: { [key: string]: string | number } | string | number): string {
    return this.getUrl(this.replacePlaceholders(path, replacement));
  }

  private getURLFromPath(path: string): URL {
    let url: URL;
    if (path.startsWith('http')) {
      url = new URL(path);
    } else {
      url = new URL(path, window.location.origin);
    }
    return url;
  }

  private replacePlaceholders(path: string, replacement: { [key: string]: string | number } | string | number): string {
    if (typeof replacement === 'string' || typeof replacement === 'number') {
      const leftBracePos = path.indexOf('{');
      const rightBracePos = path.indexOf('}', leftBracePos);
      const placeholder = path.substring(leftBracePos, rightBracePos + 1);
      return path.replace(placeholder, '' + encodeURIComponent(replacement));
    }

    const hashKeys = Object.keys(replacement);
    let result = path;
    for (const key of hashKeys) {
      result = result.replace('{' + key + '}', '' + encodeURIComponent(replacement[key]));
    }
    return result;
  }
}
